import React, { useState } from 'react';
import { History, PencilIcon, Send, Trash2 } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  AddButton,
  PageContainer,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  CloseButton,
  TealSwitch,
  EditButton,
  IconActionButton,
} from '../../Styles/AppStyles';
import { ColumnDef, DataTable } from '../../Components/Table/DataTable';
import { Header } from '../../Components/Header/Header';
import ConfirmationModal from '../../Components/Modal/ConfirmationModal';
import { Form, FormSection, SectionDescription, FormTextField } from '../../Components/Form';
import apiClient from '../../Services/ApiClient';
import useGetCustomData from '../../Hooks/useGetCustomData';
import { useSnackbar } from '../../Context/SnackbarProvider';
import UsageReportForm from './UsageReportForm';
import {
  GROUP_MODE_LABELS,
  SORT_LABELS,
  UsageReportRun,
  UsageReportSchedule,
  describeDays,
  describeRangeShort,
  formatClock,
  REPORT_TIMEZONE,
} from './usageReportTypes';

const formatDateTime = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString('en-US', {
        timeZone: REPORT_TIMEZONE,
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : '—';

const UsageReportsPage: React.FC = () => {
  const { showSnackbar } = useSnackbar();
  const [schedulesResponse, loading, , refetch] = useGetCustomData('/api/usage-reports');
  const schedules: UsageReportSchedule[] = Array.isArray(schedulesResponse) ? schedulesResponse : [];

  const [editing, setEditing] = useState<UsageReportSchedule | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<UsageReportSchedule | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [sendTarget, setSendTarget] = useState<UsageReportSchedule | null>(null);
  const [isSending, setIsSending] = useState(false);
  // Run day (UTC) to send; today by default, or an earlier day to resend/test past activity.
  const [sendAsOf, setSendAsOf] = useState(() => new Date().toISOString().slice(0, 10));
  const [historyTarget, setHistoryTarget] = useState<UsageReportSchedule | null>(null);
  const [runs, setRuns] = useState<UsageReportRun[]>([]);
  const [runsLoading, setRunsLoading] = useState(false);

  const openForm = (schedule: UsageReportSchedule | null) => {
    setEditing(schedule);
    setShowForm(true);
  };

  const handleSaved = () => {
    showSnackbar(editing ? 'Usage report updated' : 'Usage report created', 'success');
    setShowForm(false);
    setEditing(null);
    refetch();
  };

  const handleToggleActive = async (schedule: UsageReportSchedule) => {
    try {
      const res = await apiClient(`/api/usage-reports/${schedule.id}/active`, {
        method: 'PUT',
        body: JSON.stringify({ active: !schedule.active_flag }),
      });
      if (!res.ok) {
        showSnackbar('Failed to update status', 'error');
        return;
      }
      showSnackbar(`Usage report ${schedule.active_flag ? 'paused' : 'enabled'}`, 'success');
      refetch();
    } catch {
      showSnackbar('Failed to update status', 'error');
    }
  };

  const handleSendNow = async () => {
    if (!sendTarget || isSending) return;
    setIsSending(true);
    try {
      const res = await apiClient(`/api/usage-reports/${sendTarget.id}/send`, {
        method: 'POST',
        body: JSON.stringify({ as_of_date: sendAsOf }),
      });
      const data = await res.json().catch(() => ({}));
      if (data.status === 'sent') {
        showSnackbar(`Report sent to ${data.recipient_count} recipient${data.recipient_count === 1 ? '' : 's'} (${data.row_count} rows)`, 'success');
      } else if (data.status === 'partial') {
        showSnackbar(`Sent to ${data.recipient_count} recipients; some failed: ${data.errors?.[0] ?? ''}`, 'warning');
      } else {
        showSnackbar(data.errors?.[0] ?? data.message ?? 'Failed to send report', 'error');
      }
      setSendTarget(null);
      refetch();
    } catch {
      showSnackbar('Failed to send report', 'error');
    } finally {
      setIsSending(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget || isDeleting) return;
    setIsDeleting(true);
    try {
      const res = await apiClient(`/api/usage-reports/${deleteTarget.id}`, { method: 'DELETE' });
      if (!res.ok) {
        showSnackbar('Failed to delete usage report', 'error');
        return;
      }
      showSnackbar('Usage report deleted', 'success');
      setDeleteTarget(null);
      refetch();
    } catch {
      showSnackbar('Failed to delete usage report', 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const openHistory = async (schedule: UsageReportSchedule) => {
    setHistoryTarget(schedule);
    setRuns([]);
    setRunsLoading(true);
    try {
      const res = await apiClient(`/api/usage-reports/${schedule.id}/runs`);
      if (res.ok) setRuns(await res.json());
    } finally {
      setRunsLoading(false);
    }
  };

  const columns: ColumnDef<UsageReportSchedule>[] = [
    { field: 'name', headerName: 'Report', width: 220, sortable: true, searchable: true },
    {
      field: 'location_name',
      headerName: 'Location',
      width: 220,
      sortable: true,
      searchable: true,
      valueGetter: (_v, row) => `${row.location_name ?? `OVID ${row.ovid}`}${row.customer_name ? ` — ${row.customer_name}` : ''}`,
    },
    {
      field: 'run_time',
      headerName: 'Runs',
      width: 200,
      valueGetter: (_v, row) => `${describeDays(row.run_days)} at ${formatClock(row.run_time)} UTC`,
    },
    {
      field: 'range_start_time',
      headerName: 'Pulls',
      width: 300,
      valueGetter: (_v, row) => describeRangeShort(row),
    },
    {
      field: 'group_mode',
      headerName: 'Layout',
      width: 240,
      valueGetter: (_v, row: UsageReportSchedule) =>
        `${GROUP_MODE_LABELS[row.group_mode]} · by ${SORT_LABELS[row.sort_by]}${row.replenish_enabled ? ' · Replenish' : ''}`,
    },
    {
      field: 'recipients',
      headerName: 'Recipients',
      width: 120,
      valueGetter: (_v, row) => row.recipients.length,
      renderCell: (params) => (
        <span title={params.row.recipients.map((r: any) => r.email).join('\n')}>{params.row.recipients.length}</span>
      ),
    },
    {
      field: 'next_run_at',
      headerName: 'Next Run',
      width: 170,
      valueGetter: (_v, row) => (row.active_flag ? formatDateTime(row.next_run_at) : 'Paused'),
    },
    {
      field: 'last_run_status',
      headerName: 'Last Scheduled Run',
      width: 220,
      valueGetter: (_v, row) =>
        row.last_run_at
          ? `${formatDateTime(`${row.last_run_at.replace(' ', 'T')}Z`)} · ${row.last_run_status ?? ''}`
          : 'Never',
    },
    {
      field: 'id',
      headerName: 'Actions',
      width: 150,
      renderCell: (params) => (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <EditButton onClick={() => openForm(params.row)} title="Edit">
            <PencilIcon size={18} />
          </EditButton>
          <IconActionButton onClick={() => setSendTarget(params.row)} title="Send now">
            <Send size={16} />
          </IconActionButton>
          <IconActionButton onClick={() => openHistory(params.row)} title="Send history">
            <History size={16} />
          </IconActionButton>
          <IconActionButton $variant="danger" onClick={() => setDeleteTarget(params.row)} title="Delete">
            <Trash2 size={16} />
          </IconActionButton>
        </div>
      ),
    },
    {
      field: 'active_flag',
      headerName: 'Active',
      width: 90,
      renderCell: (params) => (
        <TealSwitch checked={!!params.row.active_flag} onChange={() => handleToggleActive(params.row)} />
      ),
    },
  ];

  return (
    <PageContainer>
      <Header title="Usage Reports" subtitle="Scheduled Transactions usage emails" data={schedules}>
        <AddButton onClick={() => openForm(null)} title="New usage report">+</AddButton>
      </Header>

      <DataTable columns={columns} data={schedules} idField="id" pageKey="usage-reports" loading={loading} />

      {showForm && (
        <ModalOverlay onClick={() => setShowForm(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()} style={{ maxWidth: 960 }}>
            <CloseButton onClick={() => setShowForm(false)}>×</CloseButton>
            <UsageReportForm
              schedule={editing}
              onCancel={() => setShowForm(false)}
              onSaved={handleSaved}
            />
          </ModalContent>
        </ModalOverlay>
      )}

      {historyTarget && (
        <ModalOverlay onClick={() => setHistoryTarget(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()} style={{ maxWidth: 960 }}>
            <CloseButton onClick={() => setHistoryTarget(null)}>×</CloseButton>
            <ModalHeader>Send History — {historyTarget.name}</ModalHeader>
            {runsLoading ? (
              <p>Loading...</p>
            ) : runs.length === 0 ? (
              <p>This report has not been sent yet.</p>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Sent</TableCell>
                      <TableCell>Trigger</TableCell>
                      <TableCell>Period</TableCell>
                      <TableCell align="right">Rows</TableCell>
                      <TableCell align="right">Recipients</TableCell>
                      <TableCell align="right">Replenished</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {runs.map((run) => (
                      <TableRow key={run.id}>
                        <TableCell>{formatDateTime(run.created_at)}</TableCell>
                        <TableCell>
                          {run.trigger_type === 'manual' ? `Manual${run.triggered_by_name ? ` (${run.triggered_by_name})` : ''}` : 'Schedule'}
                        </TableCell>
                        <TableCell>
                          {formatDateTime(run.range_start)} – {formatDateTime(run.range_end)}
                        </TableCell>
                        <TableCell align="right">{run.row_count}</TableCell>
                        <TableCell align="right">{run.recipient_count}</TableCell>
                        <TableCell align="right">
                          {run.replenish_lines ? `${run.replenished_lines} / ${run.replenish_lines}` : '—'}
                        </TableCell>
                        <TableCell title={run.error_message ?? ''}>{run.status}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </ModalContent>
        </ModalOverlay>
      )}

      {sendTarget && (
        <ModalOverlay onClick={() => setSendTarget(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <CloseButton onClick={() => setSendTarget(null)}>×</CloseButton>
            <Form
              embedded
              onSubmit={(e) => { e.preventDefault(); handleSendNow(); }}
              title="Send report now?"
              submitLabel={isSending ? 'Sending...' : 'Send'}
              onCancel={() => setSendTarget(null)}
              isLoading={isSending}
            >
              <FormSection>
                <SectionDescription>
                  "{sendTarget.name}" will be emailed to {sendTarget.recipients.length} recipient
                  {sendTarget.recipients.length === 1 ? '' : 's'} using the time range of the day below.
                  The regular schedule is not affected.
                </SectionDescription>
                <FormTextField
                  label="Send as of (UTC)"
                  type="date"
                  value={sendAsOf}
                  onChange={(e) => setSendAsOf(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </FormSection>
            </Form>
          </ModalContent>
        </ModalOverlay>
      )}

      {deleteTarget && (
        <ConfirmationModal
          title="Delete usage report?"
          message={`"${deleteTarget.name}" will stop sending. Replenish links in emails already sent keep working until they expire.`}
          confirmText="Delete"
          onConfirm={handleConfirmDelete}
          onCancel={() => setDeleteTarget(null)}
          isLoading={isDeleting}
          loadingText="Deleting..."
        />
      )}
    </PageContainer>
  );
};

export default UsageReportsPage;
